<template>
  <div class="showroom-form-wrapper">
    <!-- Toast Notifications -->
    <div class="toast-container">
      <CToaster :autohide="3000">
        <CToast
          v-for="(info, index) in infoList"
          :key="`toast-${index}-${Date.now()}`"
          :show="true"
          :header="info.header"
          :color="info.color"
        >
          {{ info.message }}
        </CToast>
      </CToaster>
    </div>

    <!-- Page Header -->
    <CRow class="mb-4">
      <CCol>
        <div class="page-header">
          <h4 class="mb-0">
            <i class="fas fa-store me-2 text-primary"></i>
            <strong>{{ obj.id ? 'Edit' : 'Add' }} Showroom</strong>
          </h4>
          <small class="text-muted">{{ obj.id ? 'Update existing showroom information' : 'Create a new showroom record' }}</small>
        </div>
      </CCol>
    </CRow>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="text-center py-5">
        <CSpinner color="primary" />
        <p class="text-muted mt-3">Loading showroom data...</p>
      </div>
    </div>

    <!-- Showroom Form -->
    <CRow v-else>
      <CCol sm="12">
        <CCard class="form-card">
          <CCardHeader class="form-header">
            <div class="d-flex align-items-center">
              <i class="fas fa-building me-2 text-primary"></i>
              <strong>Showroom Information</strong>
            </div>
          </CCardHeader>
          <CCardBody>
            <CForm>
              <!-- Lot No -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Lot No</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.lotNo.$model"
                    :isValid="checkIfValid('lotNo')"
                    :readonly="isReadOnly"
                    invalidFeedback="Lot number is required"
                    placeholder="Enter lot number"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-info-circle me-1"></i>
                    Lot or location number of the showroom
                  </small>
                </CCol>
              </CRow>

              <!-- Name -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Name</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.name.$model"
                    :isValid="checkIfValid('name')"
                    :readonly="isReadOnly"
                    invalidFeedback="Showroom name is required (minimum 2 characters)"
                    placeholder="Enter showroom name"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-store-alt me-1"></i>
                    Official name of the showroom
                  </small>
                </CCol>
              </CRow>

              <!-- Code -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Code</strong>
                  <span class="text-danger ms-1">*</span>
                </CCol>
                <CCol sm="9">
                  <CInput
                    v-model="$v.obj.code.$model"
                    :isValid="checkIfValid('code')"
                    :readonly="isReadOnly"
                    invalidFeedback="Code is required (minimum 2 characters)"
                    placeholder="Enter showroom code"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-code me-1"></i>
                    Unique code for the showroom
                  </small>
                </CCol>
              </CRow>

              <!-- Address -->
              <CRow class="mb-3">
                <CCol tag="label" sm="3" class="col-form-label">
                  <strong>Address</strong>
                </CCol>
                <CCol sm="9">
                  <CTextarea
                    v-model="obj.address"
                    :readonly="isReadOnly"
                    placeholder="Enter showroom address"
                    rows="4"
                  />
                  <small class="form-text text-muted">
                    <i class="fas fa-map-marker-alt me-1"></i>
                    Full address of the showroom location
                  </small>
                </CCol>
              </CRow>
            </CForm>
          </CCardBody>

          <!-- Form Footer -->
          <CCardFooter class="form-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <CButton
                  v-if="canSubmit"
                  color="primary"
                  :disabled="!isValid || submitting"
                  @click="onSubmit"
                >
                  <CSpinner v-if="submitting" size="sm" class="me-2" />
                  <i v-else class="fas fa-check-circle me-2"></i>
                  {{ submitting ? 'Saving...' : 'Submit' }}
                </CButton>
                <CButton
                  color="secondary"
                  class="ms-2"
                  @click="cancel"
                  :disabled="submitting"
                >
                  <i class="fas fa-times me-2"></i>
                  Cancel
                </CButton>
              </div>
              <div v-if="!isValid" class="text-danger">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <small>Please fill in all required fields</small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import ShowroomApi from "../../lib/showroomApi";
import Helper from "../../lib/helper";
import { required, minLength } from "vuelidate/lib/validators";

const helper = new Helper();

export default {
  name: "Showroom",
  data: () => {
    return {
      infoList: [],
      api: new ShowroomApi(),
      obj: {
        name: "",
        lotNo: "",
        code: "",
      },
      submitted: false,
      submitting: false,
      loading: false,
    };
  },
  mounted() {
    // Check view permission first
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }

    // If creating new, check CanAdd
    if (!this.$route.params.id && !this.canAdd) {
      this.$router.push('/admins/ShowroomList');
      this.toast('Access Denied', 'You do not have permission to add showrooms', 'danger');
      return;
    }

    this.obj = this.getEmptyObj();
    this.resetObj();
  },
  validations: {
    obj: {
      name: {
        required,
        minLength: minLength(2),
      },
      lotNo: {
        required,
        minLength: minLength(1),
      },
      code: {
        required,
        minLength: minLength(2),
      },
    },
  },

  computed: {
    formString() {
      return JSON.stringify(this.obj, null, 4);
    },
    isValid() {
      return !this.$v.obj.$invalid;
    },
    isDirty() {
      return this.$v.obj.$anyDirty;
    },
    canView() {
      return this.$auth.hasSubModulePermission('SHOWROOMS', 'CanView');
    },
    canAdd() {
      return this.$auth.hasSubModulePermission('SHOWROOMS', 'CanAdd');
    },
    canUpdate() {
      return this.$auth.hasSubModulePermission('SHOWROOMS', 'CanUpdate');
    },
    isReadOnly() {
      if (this.obj.id) {
        return !this.canUpdate;
      } else {
        return !this.canAdd;
      }
    },
    canSubmit() {
      if (this.obj.id) {
        return this.canUpdate;
      } else {
        return this.canAdd;
      }
    },
  },
  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.obj[fieldName];
      return !(field.$invalid || field.$model === "");
    },

    onSubmit() {
      this.submitting = true;
      if (!this.obj.id) {
        this.api
          .create(this.obj)
          .then((response) => {
            this.toast("Success", "Showroom created successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/ShowroomList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      } else {
        this.api
          .update(this.obj)
          .then((response) => {
            this.toast("Success", "Showroom updated successfully", "success");
            setTimeout(() => {
              this.$router.push({ path: "/admins/ShowroomList" });
            }, 1000);
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.submitting = false;
          });
      }
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    resetObj() {
      this.loading = true;
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.loading = false;
          })
          .catch(({ data }) => {
            this.toast("Error", helper.getErrorMessage(data), "danger");
            this.loading = false;
          });
      } else {
        this.obj = this.getEmptyObj();
        this.loading = false;
      }
    },

    getEmptyObj() {
      return {
        name: "",
        lotNo: "",
        address: "",
        phone: "",
        website: "",
        country: "",
        contactPersonName: "",
        contactPersonPhone: "",
        contactPersonEmail: "",
        code: null,
      };
    },

    cancel() {
      this.$router.push({ path: "/admins/ShowroomList" });
    },
  },
};
</script>

<style scoped>
/* Professional Container */
.showroom-form-wrapper {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Page Header */
.page-header {
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
}

.page-header h4 {
  font-size: 1.5rem;
  color: var(--cui-body-color, #212529);
}

.page-header small {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

/* Loading State */
.loading-state {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form Card */
.form-card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.form-header {
  background: white;
  border-bottom: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

.form-header strong {
  font-size: 1.1rem;
  color: var(--cui-body-color, #212529);
}

/* Form Body */
.form-card .card-body {
  padding: 2rem 1.5rem;
}

/* Form Labels */
.col-form-label {
  font-weight: 500;
  color: var(--cui-body-color, #495057);
}

.col-form-label .text-danger {
  font-size: 1.2em;
}

/* Form Help Text */
.form-text {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

/* Form Footer */
.form-footer {
  background: var(--cui-body-bg, #f8f9fa);
  border-top: 2px solid var(--cui-border-color, #d8dbe0);
  padding: 1rem 1.25rem;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .showroom-form-wrapper {
    padding: 1rem;
  }

  .page-header h4 {
    font-size: 1.25rem;
  }

  .form-card .card-body {
    padding: 1.5rem 1rem;
  }

  .col-form-label {
    margin-bottom: 0.5rem;
  }
}

/* Print Styles */
@media print {
  .toast-container,
  .form-footer {
    display: none !important;
  }

  .form-card {
    box-shadow: none;
    border: 1px solid #dee2e6;
  }
}
</style>
